import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  writeBatch,
} from 'firebase/firestore';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential, GoogleAuthProvider, reauthenticateWithPopup } from 'firebase/auth';
import { db, auth } from '../firebase/config';

const THOUGHTS_COLLECTION = 'thoughts';
const DIARIES_COLLECTION = 'diaries';
const JOURNALS_COLLECTION = 'journals';
const USERS_COLLECTION = 'users';

// Delete all documents in a collection for a user
const deleteUserDocuments = async (
  collectionName: string,
  userId: string,
  onProgress?: (deleted: number, total: number) => void
): Promise<number> => {
  const q = query(
    collection(db, collectionName),
    where('userId', '==', userId)
  );

  const querySnapshot = await getDocs(q);
  const total = querySnapshot.docs.length;
  let deleted = 0;

  // Use batched writes for efficiency (max 500 per batch)
  const batchSize = 500;
  let batch = writeBatch(db);
  let batchCount = 0;

  for (const docSnapshot of querySnapshot.docs) {
    batch.delete(docSnapshot.ref);
    batchCount++;
    deleted++;

    if (batchCount >= batchSize) {
      await batch.commit();
      batch = writeBatch(db);
      batchCount = 0;
    }

    onProgress?.(deleted, total);
  }

  // Commit remaining documents
  if (batchCount > 0) {
    await batch.commit();
  }

  return total;
};

// Delete user document from users collection
const deleteUserDocument = async (userId: string): Promise<void> => {
  const userDocRef = doc(db, USERS_COLLECTION, userId);
  await deleteDoc(userDocRef);
};

// Delete all user data from Firestore
export const deleteAllUserData = async (
  userId: string,
  onProgress?: (stage: string, current: number, total: number) => void
): Promise<{ thoughts: number; diaries: number; journals: number }> => {
  let thoughtsDeleted = 0;
  let diariesDeleted = 0;
  let journalsDeleted = 0;

  // Delete thoughts
  onProgress?.('Deleting thoughts...', 0, 0);
  thoughtsDeleted = await deleteUserDocuments(THOUGHTS_COLLECTION, userId, (deleted, total) => {
    onProgress?.('Deleting thoughts...', deleted, total);
  });

  // Delete diary entries
  onProgress?.('Deleting diary entries...', 0, 0);
  diariesDeleted = await deleteUserDocuments(DIARIES_COLLECTION, userId, (deleted, total) => {
    onProgress?.('Deleting diary entries...', deleted, total);
  });

  // Delete journal entries
  onProgress?.('Deleting journal entries...', 0, 0);
  journalsDeleted = await deleteUserDocuments(JOURNALS_COLLECTION, userId, (deleted, total) => {
    onProgress?.('Deleting journal entries...', deleted, total);
  });

  // Delete user document
  onProgress?.('Deleting user settings...', 0, 0);
  await deleteUserDocument(userId);

  return {
    thoughts: thoughtsDeleted,
    diaries: diariesDeleted,
    journals: journalsDeleted,
  };
};

// Delete the Firebase Auth user account
export const deleteUserAccount = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user is currently signed in');
  }

  try {
    await deleteUser(user);
  } catch (error: any) {
    // If the error is due to requiring recent authentication, we need to re-authenticate
    if (error.code === 'auth/requires-recent-login') {
      throw new Error('Please sign out and sign in again before deleting your account');
    }
    throw error;
  }
};

// Complete account deletion process
export const deleteAccount = async (
  onProgress?: (stage: string, current: number, total: number) => void
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user is currently signed in');
  }

  // Step 1: Delete all user data from Firestore
  await deleteAllUserData(user.uid, onProgress);

  // Step 2: Delete the Firebase Auth user account
  onProgress?.('Deleting account...', 0, 0);
  await deleteUserAccount();
};

